import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminLogoutMutation } from '../../store/api/adminApi';
import { logout } from '../../store/slices/authSlice';
import { useAppDispatch } from '../../hooks/redux';
import { confirmLogout } from '../../utils/confirm';
import { showSuccess, showError } from '../../utils/toast';
import { getAppType, AppType } from '../../utils/appType';
import logo from '../../assets/images/logo.png';
import favicon from '../../assets/images/favicon.png';
import Image from '../Image';



const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [logoutMutation] = useAdminLogoutMutation();
  const [appType, setAppType] = useState<AppType>(getAppType());

  useEffect(() => {
    setAppType(getAppType());
  }, []);

  // Helper function to determine if a menu item should be visible based on app type
  const shouldShowMenuItem = (menuKey: string): boolean => {
    // Define which menu items should be shown for each app type
    const educationMenus = [
      'dashboard', 'users', 'doctors', 'items', 'articles', 'exhibitions', 
      'activities', 'pages', 'subscription', 'platform', 'settings', 'contacts', 
      'access', 'account', 'logout', 'notifications'
    ];
    
    const tradeMenus = [
      'trade-dashboard', 'users', 
      'settings', 'contacts', 'access', 'account', 'logout', 'ads', 'platform-trade',
      'fesilah-cards', 'fesilah-vouchers', 'reviews', 'notifications'
    ];

    if (appType === 'education') {
      return educationMenus.includes(menuKey);
    } else if (appType === 'trade') {
      return tradeMenus.includes(menuKey);
    }
    
    return true; // Default: show all
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  const isMenuExpanded = (menu: string) => {
    return expandedMenus.includes(menu);
  };

  const toggleMenu = (menu: string) => {
    setExpandedMenus(prev =>
      prev.includes(menu) ? prev.filter(m => m !== menu) : [...prev, menu]
    );
  };

  const isParentActive = (paths: string[]) => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  const handleLogout = async () => {
    const confirmed = await confirmLogout();
    if (confirmed) {
      try {
        await logoutMutation(undefined).unwrap();
        dispatch(logout());
        showSuccess('Logged out successfully!');
        navigate('/admin/login');
      } catch (err: any) {
        showError(err?.data?.message || 'Logout failed. Please try again.');
      }
    }
  };

  return (
    <div id="kt_app_sidebar" className="app-sidebar flex-column" style={{ height: '100vh', overflowY: 'auto' }} data-kt-drawer="true" data-kt-drawer-name="app-sidebar" data-kt-drawer-activate="{default: true, lg: false}" data-kt-drawer-overlay="true" data-kt-drawer-width="225px" data-kt-drawer-direction="start" data-kt-drawer-toggle="#kt_app_sidebar_mobile_toggle">
      <div className="app-sidebar-logo px-6 bg-white" id="kt_app_sidebar_logo">
        <Link to="/admin/dashboard">
          <img alt="Logo" src={logo} className="h-60px text-center app-sidebar-logo-default" />
          <img alt="Logo" src={favicon} className="h-40px app-sidebar-logo-minimize" />
        </Link>
        <div id="kt_app_sidebar_toggle" className="app-sidebar-toggle btn btn-icon btn-shadow btn-sm btn-color-muted btn-active-color-primary body-bg h-30px w-30px position-absolute top-50 start-100 translate-middle rotate" data-kt-toggle="true" data-kt-toggle-state="active" data-kt-toggle-target="body" data-kt-toggle-name="app-sidebar-minimize">
          <span className="svg-icon svg-icon-2 rotate-180">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path opacity="0.5" d="M14.2657 11.4343L18.45 7.25C18.8642 6.83579 18.8642 6.16421 18.45 5.75C18.0358 5.33579 17.3642 5.33579 16.95 5.75L11.4071 11.2929C11.0166 11.6834 11.0166 12.3166 11.4071 12.7071L16.95 18.25C17.3642 18.6642 18.0358 18.6642 18.45 18.25C18.8642 17.8358 18.8642 17.1642 18.45 16.75L14.2657 12.5657C13.9533 12.2533 13.9533 11.7467 14.2657 11.4343Z" fill="currentColor" />
              <path d="M8.2657 11.4343L12.45 7.25C12.8642 6.83579 12.8642 6.16421 12.45 5.75C12.0358 5.33579 11.3642 5.33579 10.95 5.75L5.40712 11.2929C5.01659 11.6834 5.01659 12.3166 5.40712 12.7071L10.95 18.25C11.3642 18.6642 12.0358 18.6642 12.45 18.25C12.8642 17.8358 12.8642 17.1642 12.45 16.75L8.2657 12.5657C7.95328 12.2533 7.95328 11.7467 8.2657 11.4343Z" fill="currentColor" />
            </svg>
          </span>
        </div>
      </div>
      
      <div className="app-sidebar-menu overflow-hidden flex-column-fluid">
        <div id="kt_app_sidebar_menu_wrapper" className="app-sidebar-wrapper hover-scroll-overlay-y my-5" data-kt-scroll="true" data-kt-scroll-activate="true" data-kt-scroll-height="auto" data-kt-scroll-dependencies="#kt_app_sidebar_logo, #kt_app_sidebar_footer" data-kt-scroll-wrappers="#kt_app_sidebar_menu" data-kt-scroll-offset="5px" data-kt-scroll-save-state="true">
          <div className="menu menu-column menu-rounded menu-sub-indention px-3" id="#kt_app_sidebar_menu" data-kt-menu="true" data-kt-menu-expand="false">
            {shouldShowMenuItem('dashboard') && (
            <div className="menu-item">
              <Link to="/admin/dashboard" className={`menu-link ${isActive('/admin/dashboard')}`}>
                <span className="menu-icon">
                  <span className="svg-icon svg-icon-2">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="9" height="9" rx="2" fill="currentColor" />
                      <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="currentColor" />
                      <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="currentColor" />
                      <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="currentColor" />
                    </svg>
                  </span>
                </span>
                <span className="menu-title">Dashboard</span>
              </Link>
            </div>
            )}

            {shouldShowMenuItem('trade-dashboard') && (
              <div className="menu-item">
                <Link to="/admin/trade-dashboard" className={`menu-link ${isActive('/admin/trade-dashboard')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="2" width="9" height="9" rx="2" fill="currentColor" />
                        <rect opacity="0.3" x="13" y="2" width="9" height="9" rx="2" fill="currentColor" />
                        <rect opacity="0.3" x="13" y="13" width="9" height="9" rx="2" fill="currentColor" />
                        <rect opacity="0.3" x="2" y="13" width="9" height="9" rx="2" fill="currentColor" />
                      </svg>
                    </span>
                  </span>
                  <span className="menu-title"> Dashboard</span>
                </Link>
              </div>
            )}
            
            <div className="menu-item pt-5">
              <div className="menu-content">
                <span className="menu-heading fw-bold text-uppercase fs-7">Navigation Links</span>
              </div>
            </div>
            
            <div className="menu-item">
              <Link to="/admin/users" className={`menu-link ${isActive('/admin/users')}`}>
                <span className="menu-icon">
                  <span className="svg-icon svg-icon-2">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="currentColor" />
                      <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="currentColor" />
                    </svg>
                  </span>
                </span>
                <span className="menu-title">Customers Management</span>
              </Link>
            </div>

            {shouldShowMenuItem('doctors') && (
              <div className="menu-item">
                <Link to="/admin/doctors" className={`menu-link ${isActive('/admin/doctors')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="currentColor" />
                        <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="currentColor" />
                      </svg>
                    </span>
                  </span>
                  <span className="menu-title">Doctors Management</span>
                </Link>
              </div>
            )}



            {shouldShowMenuItem('items') && (
              <div className="menu-item">
                <Link to="/admin/items" className={`menu-link ${isActive('/admin/items')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-seedling"></i>
                    </span>
                  </span>
                  <span className="menu-title">Plants Management</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('articles') && (
              <div className="menu-item">
                <Link to="/admin/articles" className={`menu-link ${isActive('/admin/articles')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.2929 2.70711C11.6834 2.31658 12.3166 2.31658 12.7071 2.70711L15.2929 5.29289C15.6834 5.68342 15.6834 6.31658 15.2929 6.70711L12.7071 9.29289C12.3166 9.68342 11.6834 9.68342 11.2929 9.29289L8.70711 6.70711C8.31658 6.31658 8.31658 5.68342 8.70711 5.29289L11.2929 2.70711Z" fill="currentColor"></path>
                        <path d="M11.2929 14.7071C11.6834 14.3166 12.3166 14.3166 12.7071 14.7071L15.2929 17.2929C15.6834 17.6834 15.6834 18.3166 15.2929 18.7071L12.7071 21.2929C12.3166 21.6834 11.6834 21.6834 11.2929 21.2929L8.70711 18.7071C8.31658 18.3166 8.31658 17.6834 8.70711 17.2929L11.2929 14.7071Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M5.29289 8.70711C5.68342 8.31658 6.31658 8.31658 6.70711 8.70711L9.29289 11.2929C9.68342 11.6834 9.68342 12.3166 9.29289 12.7071L6.70711 15.2929C6.31658 15.6834 5.68342 15.6834 5.29289 15.2929L2.70711 12.7071C2.31658 12.3166 2.31658 11.6834 2.70711 11.2929L5.29289 8.70711Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M17.2929 8.70711C17.6834 8.31658 18.3166 8.31658 18.7071 8.70711L21.2929 11.2929C21.6834 11.6834 21.6834 12.3166 21.2929 12.7071L18.7071 15.2929C18.3166 15.6834 17.6834 15.6834 17.2929 15.2929L14.7071 12.7071C14.3166 12.3166 14.3166 11.6834 14.7071 11.2929L17.2929 8.70711Z" fill="currentColor"></path>
                      </svg>
                    </span>
                  </span>
                  <span className="menu-title">Articles Management</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('exhibitions') && (
              <div className="menu-item">
                <Link to="/admin/exhibitions" className={`menu-link ${isActive('/admin/exhibitions')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.2929 2.70711C11.6834 2.31658 12.3166 2.31658 12.7071 2.70711L15.2929 5.29289C15.6834 5.68342 15.6834 6.31658 15.2929 6.70711L12.7071 9.29289C12.3166 9.68342 11.6834 9.68342 11.2929 9.29289L8.70711 6.70711C8.31658 6.31658 8.31658 5.68342 8.70711 5.29289L11.2929 2.70711Z" fill="currentColor"></path>
                        <path d="M11.2929 14.7071C11.6834 14.3166 12.3166 14.3166 12.7071 14.7071L15.2929 17.2929C15.6834 17.6834 15.6834 18.3166 15.2929 18.7071L12.7071 21.2929C12.3166 21.6834 11.6834 21.6834 11.2929 21.2929L8.70711 18.7071C8.31658 18.3166 8.31658 17.6834 8.70711 17.2929L11.2929 14.7071Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M5.29289 8.70711C5.68342 8.31658 6.31658 8.31658 6.70711 8.70711L9.29289 11.2929C9.68342 11.6834 9.68342 12.3166 9.29289 12.7071L6.70711 15.2929C6.31658 15.6834 5.68342 15.6834 5.29289 15.2929L2.70711 12.7071C2.31658 12.3166 2.31658 11.6834 2.70711 11.2929L5.29289 8.70711Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M17.2929 8.70711C17.6834 8.31658 18.3166 8.31658 18.7071 8.70711L21.2929 11.2929C21.6834 11.6834 21.6834 12.3166 21.2929 12.7071L18.7071 15.2929C18.3166 15.6834 17.6834 15.6834 17.2929 15.2929L14.7071 12.7071C14.3166 12.3166 14.3166 11.6834 14.7071 11.2929L17.2929 8.70711Z" fill="currentColor"></path>
                      </svg>
                    </span>
                  </span>
                  <span className="menu-title">Exhibitions Management</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('activities') && (
              <div className="menu-item">
                <Link to="/admin/activities" className={`menu-link ${isActive('/admin/activities')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.2929 2.70711C11.6834 2.31658 12.3166 2.31658 12.7071 2.70711L15.2929 5.29289C15.6834 5.68342 15.6834 6.31658 15.2929 6.70711L12.7071 9.29289C12.3166 9.68342 11.6834 9.68342 11.2929 9.29289L8.70711 6.70711C8.31658 6.31658 8.31658 5.68342 8.70711 5.29289L11.2929 2.70711Z" fill="currentColor"></path>
                        <path d="M11.2929 14.7071C11.6834 14.3166 12.3166 14.3166 12.7071 14.7071L15.2929 17.2929C15.6834 17.6834 15.6834 18.3166 15.2929 18.7071L12.7071 21.2929C12.3166 21.6834 11.6834 21.6834 11.2929 21.2929L8.70711 18.7071C8.31658 18.3166 8.31658 17.6834 8.70711 17.2929L11.2929 14.7071Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M5.29289 8.70711C5.68342 8.31658 6.31658 8.31658 6.70711 8.70711L9.29289 11.2929C9.68342 11.6834 9.68342 12.3166 9.29289 12.7071L6.70711 15.2929C6.31658 15.6834 5.68342 15.6834 5.29289 15.2929L2.70711 12.7071C2.31658 12.3166 2.31658 11.6834 2.70711 11.2929L5.29289 8.70711Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M17.2929 8.70711C17.6834 8.31658 18.3166 8.31658 18.7071 8.70711L21.2929 11.2929C21.6834 11.6834 21.6834 12.3166 21.2929 12.7071L18.7071 15.2929C18.3166 15.6834 17.6834 15.6834 17.2929 15.2929L14.7071 12.7071C14.3166 12.3166 14.3166 11.6834 14.7071 11.2929L17.2929 8.70711Z" fill="currentColor"></path>
                      </svg>
                    </span>
                  </span>
                  <span className="menu-title">Activities Management</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('ads') && (
              <div className="menu-item">
                <Link to="/admin/ads" className={`menu-link ${isActive('/admin/ads')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-ad"></i>
                    </span>
                  </span>
                  <span className="menu-title">Ads Management</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('fesilah-cards') && (
              <div className="menu-item">
                <Link to="/admin/fesilah-cards" className={`menu-link ${isActive('/admin/fesilah-cards')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-credit-card"></i>
                    </span>
                  </span>
                  <span className="menu-title">Fesilah Cards</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('fesilah-vouchers') && (
              <div className="menu-item">
                <Link to="/admin/fesilah-vouchers" className={`menu-link ${isActive('/admin/fesilah-vouchers')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-ticket-alt"></i>
                    </span>
                  </span>
                  <span className="menu-title">Fesilah Vouchers</span>
                </Link>
              </div>
            )}
            {shouldShowMenuItem('reviews') && (
              <div className="menu-item">
                <Link to="/admin/reviews" className={`menu-link ${isActive('/admin/reviews')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-star"></i>
                    </span>
                  </span>
                  <span className="menu-title">Reviews Management</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('notifications') && (
              <div className="menu-item">
                <Link to="/admin/notifications" className={`menu-link ${isActive('/admin/notifications')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-paper-plane"></i>
                    </span>
                  </span>
                  <span className="menu-title">Send Notifications</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('subscription') && (
              <div className={`menu-item menu-accordion ${isMenuExpanded('subscription') || isParentActive(['/admin/packages', '/admin/subscription-history', '/admin/stripe-logs']) ? 'show' : ''}`}>
                <span className="menu-link" onClick={() => toggleMenu('subscription')} style={{ cursor: 'pointer' }}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-tags"></i>
                    </span>
                  </span>
                  <span className="menu-title">Subscription Management</span>
                  <span className="menu-arrow"></span>
                </span>
                <div className={`menu-sub menu-sub-accordion ${isMenuExpanded('subscription') || isParentActive(['/admin/packages', '/admin/subscription-history', '/admin/stripe-logs']) ? 'show' : ''}`}>
                  <div className="menu-item">
                    <Link to="/admin/packages" className={`menu-link ${isActive('/admin/packages')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Subscription Plans</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/subscription-history" className={`menu-link ${isActive('/admin/subscription-history')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Subscription History</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/stripe-logs" className={`menu-link ${isActive('/admin/stripe-logs')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">AutoRenewal Logs</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {shouldShowMenuItem('platform') && (
              <div className={`menu-item menu-accordion ${isMenuExpanded('platform') || isParentActive(['/admin/categories', '/admin/topics', '/admin/countries', '/admin/cities', '/admin/pages']) ? 'show' : ''}`}>
                <span className="menu-link" onClick={() => toggleMenu('platform')} style={{ cursor: 'pointer' }}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-cubes"></i>
                    </span>
                  </span>
                  <span className="menu-title">Platform Management</span>
                  <span className="menu-arrow"></span>
                </span>
                <div className={`menu-sub menu-sub-accordion ${isMenuExpanded('platform') || isParentActive(['/admin/categories', '/admin/topics', '/admin/countries', '/admin/cities', '/admin/pages']) ? 'show' : ''}`}>
                  <div className="menu-item">
                    <Link to="/admin/categories" className={`menu-link ${isActive('/admin/categories')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Categories</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/topics" className={`menu-link ${isActive('/admin/topics')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Topic</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/countries" className={`menu-link ${isActive('/admin/countries')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Countries</span>
                    </Link>
                  </div>
                  {/* <div className="menu-item">
                    <Link to="/admin/cities" className={`menu-link ${isActive('/admin/cities')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Cities</span>
                    </Link>
                  </div> */}
                  <div className="menu-item">
                    <Link to="/admin/interests" className={`menu-link ${isActive('/admin/interests')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Interests</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/pages" className={`menu-link ${isActive('/admin/pages')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Pages Management</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {shouldShowMenuItem('interests') && (
              <div className="menu-item">
                <Link to="/admin/interests" className={`menu-link ${isActive('/admin/interests')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.2929 2.70711C11.6834 2.31658 12.3166 2.31658 12.7071 2.70711L15.2929 5.29289C15.6834 5.68342 15.6834 6.31658 15.2929 6.70711L12.7071 9.29289C12.3166 9.68342 11.6834 9.68342 11.2929 9.29289L8.70711 6.70711C8.31658 6.31658 8.31658 5.68342 8.70711 5.29289L11.2929 2.70711Z" fill="currentColor"></path>
                        <path d="M11.2929 14.7071C11.6834 14.3166 12.3166 14.3166 12.7071 14.7071L15.2929 17.2929C15.6834 17.6834 15.6834 18.3166 15.2929 18.7071L12.7071 21.2929C12.3166 21.6834 11.6834 21.6834 11.2929 21.2929L8.70711 18.7071C8.31658 18.3166 8.31658 17.6834 8.70711 17.2929L11.2929 14.7071Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M5.29289 8.70711C5.68342 8.31658 6.31658 8.31658 6.70711 8.70711L9.29289 11.2929C9.68342 11.6834 9.68342 12.3166 9.29289 12.7071L6.70711 15.2929C6.31658 15.6834 5.68342 15.6834 5.29289 15.2929L2.70711 12.7071C2.31658 12.3166 2.31658 11.6834 2.70711 11.2929L5.29289 8.70711Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M17.2929 8.70711C17.6834 8.31658 18.3166 8.31658 18.7071 8.70711L21.2929 11.2929C21.6834 11.6834 21.6834 12.3166 21.2929 12.7071L18.7071 15.2929C18.3166 15.6834 17.6834 15.6834 17.2929 15.2929L14.7071 12.7071C14.3166 12.3166 14.3166 11.6834 14.7071 11.2929L17.2929 8.70711Z" fill="currentColor"></path>
                      </svg>
                    </span>
                  </span>
                  <span className="menu-title">Interests</span>
                </Link>
              </div>
            )}

            {shouldShowMenuItem('platform-trade') && (
              <div className={`menu-item menu-accordion ${isMenuExpanded('platform-trade') || isParentActive(['/admin/ad-categories', '/admin/ad-types', '/admin/ad-packages', '/admin/agricultural-influencers']) ? 'show' : ''}`}>
                <span className="menu-link" onClick={() => toggleMenu('platform-trade')} style={{ cursor: 'pointer' }}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-cubes"></i>
                    </span>
                  </span>
                  <span className="menu-title">Platform Management</span>
                  <span className="menu-arrow"></span>
                </span>
                <div className={`menu-sub menu-sub-accordion ${isMenuExpanded('platform-trade') || isParentActive(['/admin/ad-categories', '/admin/ad-types', '/admin/ad-packages', '/admin/agricultural-influencers']) ? 'show' : ''}`}>
                  <div className="menu-item">
                    <Link to="/admin/ad-categories" className={`menu-link ${isActive('/admin/ad-categories')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Ad Category</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/ad-types" className={`menu-link ${isActive('/admin/ad-types')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Ad Type</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/ad-packages" className={`menu-link ${isActive('/admin/ad-packages')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Ad Packages</span>
                    </Link>
                  </div>
                  <div className="menu-item">
                    <Link to="/admin/agricultural-influencers" className={`menu-link ${isActive('/admin/agricultural-influencers')}`}>
                      <span className="menu-bullet">
                        <span className="bullet bullet-dot"></span>
                      </span>
                      <span className="menu-title">Agricultural Influencers</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className={`menu-item menu-accordion ${isMenuExpanded('settings') || isParentActive(['/admin/settings', '/admin/banners']) ? 'show' : ''}`}>
              <span className={`menu-link ${isActive('/admin/settings') || isActive('/admin/banners') ? 'active' : ''}`} onClick={() => toggleMenu('settings')} style={{ cursor: 'pointer' }}>
                <span className="menu-icon">
                  <span className="svg-icon svg-icon-2">
                    <i className="fas fa-gear"></i>
                  </span>
                </span>
                <span className="menu-title">App Settings</span>
                <span className="menu-arrow"></span>
              </span>
              <div className={`menu-sub menu-sub-accordion ${isMenuExpanded('settings') || isParentActive(['/admin/settings', '/admin/banners']) ? 'show' : ''}`}>
                <div className="menu-item">
                  <Link to="/admin/settings" className={`menu-link ${isActive('/admin/settings')}`}>
                    <span className="menu-bullet">
                      <span className="bullet bullet-dot"></span>
                    </span>
                    <span className="menu-title">Settings</span>
                  </Link>
                </div>
                <div className="menu-item">
                  <Link to="/admin/banners" className={`menu-link ${isActive('/admin/banners')}`}>
                    <span className="menu-bullet">
                      <span className="bullet bullet-dot"></span>
                    </span>
                    <span className="menu-title">App Banners</span>
                  </Link>
                </div>
              </div>
            </div>
            {shouldShowMenuItem('inquiries') && (
              <div className="menu-item">
                <Link to="/admin/inquiries" className={`menu-link ${isActive('/admin/inquiries')}`}>
                  <span className="menu-icon">
                    <span className="svg-icon svg-icon-2">
                      <i className="fas fa-users"></i>
                    </span>
                  </span>
                  <span className="menu-title">Website Inquries</span>
                </Link>
              </div>
            )}

            <div className={`menu-item menu-accordion ${isMenuExpanded('access') || isParentActive(['/admin/system-users', '/admin/roles', '/admin/permissions']) ? 'show' : ''}`}>
              <span className="menu-link" onClick={() => toggleMenu('access')} style={{ cursor: 'pointer' }}>
                <span className="menu-icon">
                  <span className="svg-icon svg-icon-2">
                    <i className="fas fa-shield-alt"></i>
                  </span>
                </span>
                <span className="menu-title">Access Control</span>
                <span className="menu-arrow"></span>
              </span>
              <div className={`menu-sub menu-sub-accordion ${isMenuExpanded('access') || isParentActive(['/admin/system-users', '/admin/roles', '/admin/permissions']) ? 'show' : ''}`}>
                <div className="menu-item">
                  <Link to="/admin/system-users" className={`menu-link ${isActive('/admin/system-users')}`}>
                    <span className="menu-bullet">
                      <span className="bullet bullet-dot"></span>
                    </span>
                    <span className="menu-title">System Users</span>
                  </Link>
                </div>
                <div className="menu-item">
                  <Link to="/admin/roles" className={`menu-link ${isActive('/admin/roles')}`}>
                    <span className="menu-bullet">
                      <span className="bullet bullet-dot"></span>
                    </span>
                    <span className="menu-title">Roles</span>
                  </Link>
                </div>
                {/* <div className="menu-item">
                  <Link to="/admin/permissions" className={`menu-link ${isActive('/admin/permissions')}`}>
                    <span className="menu-bullet">
                      <span className="bullet bullet-dot"></span>
                    </span>
                    <span className="menu-title">Permissions</span>
                  </Link>
                </div> */}
              </div>
            </div>

            <div className="menu-item">
              <Link to="/admin/account" className={`menu-link ${isActive('/admin/account')}`}>
                <span className="menu-icon">
                  <span className="svg-icon svg-icon-2">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="currentColor" />
                      <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="currentColor" />
                    </svg>
                  </span> 
                </span>
                <span className="menu-title">Account</span>
              </Link>
            </div>

       
            <div className="menu-item">
              <button 
                onClick={handleLogout} 
                className="menu-link border-0 bg-transparent w-100 text-start" 
                style={{ cursor: 'pointer' }}
              >
                <span className="menu-icon">
                  <span className="svg-icon svg-icon-2">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H19C19.6 2 20 2.4 20 3V21C20 21.6 19.6 22 19 22Z" fill="currentColor" />
                      <path d="M11 17L16 12L11 7V10H2V14H11V17Z" fill="currentColor" />
                    </svg>
                  </span>
                </span>
                <span className="menu-title">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
